import { lazy, Suspense, useEffect, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/lib/i18n";
import CookieBanner from "./components/CookieBanner";
import Gateway from "./pages/Gateway.tsx";

const AITeams = lazy(() => import("./pages/AITeams.tsx"));
const Web3Audit = lazy(() => import("./pages/Web3Audit.tsx"));
const Advisory = lazy(() => import("./pages/Advisory.tsx"));
const Hire = lazy(() => import("./pages/Hire.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const RouteFallback = () => <div className="min-h-screen bg-background" />;

const ScrollToHash = () => {
  const { hash, pathname } = useLocation();
  const prevPath = useRef<string | null>(null);
  useEffect(() => {
    if (!hash) {
      prevPath.current = pathname;
      return;
    }
    const isCrossPage = prevPath.current !== pathname;
    prevPath.current = pathname;
    // Same-page hash click: let browser handle natively (respects scroll-margin-top) — avoids double-scroll bounce
    if (!isCrossPage) return;

    const id = hash.slice(1);
    const scroll = (behavior: ScrollBehavior) => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior, block: "start" });
      return !!el;
    };
    let attempts = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const poll = () => {
      if (scroll("smooth") || attempts >= 30) {
        // Silent correction for async layout shifts (lazy Cal iframe, images pushing #book down)
        timers.push(setTimeout(() => scroll("auto"), 800));
        return;
      }
      attempts++;
      timers.push(setTimeout(poll, 100));
    };
    timers.push(setTimeout(poll, 50));
    return () => timers.forEach(clearTimeout);
  }, [hash, pathname]);
  return null;
};

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        <Suspense fallback={<RouteFallback />}>
          <Routes location={location}>
            {/* English routes */}
            <Route path="/" element={<Gateway />} />
            <Route path="/web3" element={<Web3Audit />} />
            <Route path="/ai-teams" element={<AITeams />} />
            <Route path="/1-1" element={<Advisory />} />
            <Route path="/hire" element={<Hire />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* 301 redirects from old slugs */}
            <Route path="/web3-audit" element={<Navigate to="/web3" replace />} />
            <Route path="/advisory" element={<Navigate to="/1-1" replace />} />

            {/* Polish routes */}
            <Route path="/pl" element={<Gateway />} />
            <Route path="/pl/web3" element={<Web3Audit />} />
            <Route path="/pl/ai-teams" element={<AITeams />} />
            <Route path="/pl/1-1" element={<Advisory />} />
            <Route path="/pl/hire" element={<Hire />} />
            <Route path="/pl/privacy" element={<PrivacyPolicy />} />

            {/* Polish 301 redirects */}
            <Route path="/pl/web3-audit" element={<Navigate to="/pl/web3" replace />} />
            <Route path="/pl/advisory" element={<Navigate to="/pl/1-1" replace />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.main>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LanguageProvider>
          <ScrollToHash />
          <AnimatedRoutes />
          <CookieBanner />
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
